import { SpaceObjectRequest } from '@entities/SpaceObject/schemas';
import { SpaceObjectFront } from '@entities/SpaceObject/type';
import { ENTITIES } from '@entities/enum';
import { FilterGroup } from '@features/ui-filters/components/types';
import { spaceObjectsApi } from '@services/api/endpoints/spaceobjects';
import { ACTIONS, SortOrder } from '@root/src/types/common';
import { mutationOptions, QueryClient, QueryKey, queryOptions } from '@tanstack/react-query';

/**
 * Space objects in the React Query cache: under which key each space object read lives,
 * and which reads each space object write leaves stale. Components and hooks read and
 * write space objects through this module only; no space object key is spelled anywhere
 * else.
 */

/** A page of `GET /spaceobjects`, exactly as it is requested: the key holds what the fetch sends. */
export type SpaceObjectListParams = {
  page: number;
  pageSize?: number;
  filters: FilterGroup;
  sort?: { field: string; order: SortOrder };
  memberContext?: string;
};

export type PointOfContactAssignment = { pointOfContactId: string; spaceObjectIds: string[] };

type BulkEditResult = Awaited<ReturnType<typeof spaceObjectsApi.bulkEditSpaceObjects>>;

/**
 * Every key starts with `spaceObjectKeys.all`, then names the resource. A write invalidates
 * a resource scope (`detail(id)`, `lists()`), which prefix-matches every read of that
 * resource and nothing else:
 * - `lists()` is every list page: inventory, the SST publish search, a member's objects and
 *   their tab counts, a point of contact's objects;
 * - `myLists()` is the profile's own list ("my space objects"). It has always been refreshed
 *   apart from the other lists: by a user save, and by no space object write;
 * - `detail(id)` is one space object.
 */
export const spaceObjectKeys = {
  all: ['space-objects'] as const,
  lists: () => [...spaceObjectKeys.all, 'list'] as const,
  list: (params: SpaceObjectListParams) => [...spaceObjectKeys.lists(), params] as const,
  myLists: () => [...spaceObjectKeys.all, 'my-list'] as const,
  myList: (params: SpaceObjectListParams) => [...spaceObjectKeys.myLists(), params] as const,
  detail: (spaceObjectId: string) => [...spaceObjectKeys.all, 'detail', spaceObjectId] as const,
};

const THIRTY_SECONDS = 30 * 1000;
const ONE_MINUTE = 60 * 1000;

/**
 * Every space object read. What belongs to the read itself lives here: key, fetch, stale
 * time, and `enabled` only as "no id, no fetch". A caller that adds its own `enabled` flag,
 * retry policy or stale time spreads these options and keeps that guard.
 */
export const spaceObjectQueries = {
  /**
   * One space object, with its latest SST of each type (backend
   * `src/modules/back/spaceObjects/router.js:64`). The stale time is the caller's: the
   * object, edit, SST and publish pages cache it for 30 s, the admin events for the default.
   */
  detail: (spaceObjectId: string) =>
    queryOptions({
      queryKey: spaceObjectKeys.detail(spaceObjectId),
      queryFn: () => spaceObjectsApi.getSpaceObjectById(spaceObjectId),
      enabled: !!spaceObjectId,
    }),
  /** Every list page is read with a 30 s stale time. */
  list: (params: SpaceObjectListParams) =>
    queryOptions({
      queryKey: spaceObjectKeys.list(params),
      queryFn: () => spaceObjectsApi.getSpaceObjects(params),
      staleTime: THIRTY_SECONDS,
    }),
  /**
   * How many space objects match: a one-item page, read for its `total` (the member page's
   * tab badges). It is a list page, so every write that refreshes the lists refreshes it.
   */
  count: (filters: FilterGroup) =>
    queryOptions({
      queryKey: spaceObjectKeys.list({ page: 1, pageSize: 1, filters }),
      queryFn: () => spaceObjectsApi.getSpaceObjects({ page: 1, pageSize: 1, filters }),
      staleTime: ONE_MINUTE,
    }),
  /** The profile's own list. */
  myList: (params: SpaceObjectListParams) =>
    queryOptions({
      queryKey: spaceObjectKeys.myList(params),
      queryFn: () => spaceObjectsApi.getSpaceObjects(params),
      staleTime: THIRTY_SECONDS,
    }),
};

const invalidate = async (queryClient: QueryClient, keys: QueryKey[]): Promise<void> => {
  await Promise.all(keys.map(queryKey => queryClient.invalidateQueries({ queryKey })));
};

/**
 * What each space object write leaves stale: the one place a space object write's refresh
 * targets are declared. The mutations below call them; a write another entity runs
 * (publishing an SST, saving a user) calls its entry here.
 */
export const spaceObjectInvalidations = {
  /** The new object shows in the lists, and its page opens from the saved response. */
  created: (queryClient: QueryClient, spaceObject: SpaceObjectFront) => {
    if (spaceObject.id) queryClient.setQueryData(spaceObjectKeys.detail(spaceObject.id), spaceObject);
    return invalidate(queryClient, [spaceObjectKeys.lists()]);
  },
  updated: (queryClient: QueryClient, spaceObjectId: string) =>
    invalidate(queryClient, [spaceObjectKeys.detail(spaceObjectId), spaceObjectKeys.lists()]),
  /** The deleted object leaves the lists. */
  deleted: (queryClient: QueryClient) => invalidate(queryClient, [spaceObjectKeys.lists()]),
  /** The lists show the point of contact; the objects' own pages are left to their stale time. */
  pointOfContactAssigned: (queryClient: QueryClient) => invalidate(queryClient, [spaceObjectKeys.lists()]),
  /**
   * An SST with a status event was published on the object: its page shows the latest SST
   * of each type (backend `src/modules/back/spaceObjects/controller.js:331-333`).
   */
  sstPublished: (queryClient: QueryClient, spaceObjectId: string) =>
    invalidate(queryClient, [spaceObjectKeys.detail(spaceObjectId), spaceObjectKeys.lists()]),
  /** A user was saved: the profile's own list shows its point of contact. */
  userSaved: (queryClient: QueryClient) => invalidate(queryClient, [spaceObjectKeys.myLists()]),
};

/**
 * Every space object write. Each refreshes through `spaceObjectInvalidations`, on the client
 * that ran it (`useMutation`'s own client, handed to `onSuccess`), so
 * `useMutation(spaceObjectMutations.x())` needs no `useQueryClient`.
 *
 * The refresh is requested, not awaited: none of these writes' callers (redirecting,
 * closing the confirmation) ever waited for the refetch, and awaiting it would keep the
 * mutation pending until the lists came back.
 */
export const spaceObjectMutations = {
  create: () =>
    mutationOptions<SpaceObjectFront, Error, SpaceObjectRequest>({
      mutationFn: request => spaceObjectsApi.createSpaceObject(request),
      meta: { entity: ENTITIES.SPACE_OBJECT, action: ACTIONS.CREATE },
      onSuccess: (spaceObject, _, __, { client }) => {
        void spaceObjectInvalidations.created(client, spaceObject);
      },
    }),
  update: (spaceObjectId: string) =>
    mutationOptions<SpaceObjectFront, Error, SpaceObjectRequest>({
      mutationFn: request => spaceObjectsApi.updateSpaceObject(request, spaceObjectId),
      meta: { entity: ENTITIES.SPACE_OBJECT, action: ACTIONS.UPDATE },
      onSuccess: (_, __, ___, { client }) => {
        void spaceObjectInvalidations.updated(client, spaceObjectId);
      },
    }),
  delete: () =>
    mutationOptions<SpaceObjectFront, Error, string>({
      mutationFn: spaceObjectId => spaceObjectsApi.deleteSpaceObject(spaceObjectId),
      meta: { entity: ENTITIES.SPACE_OBJECT, action: ACTIONS.DELETE },
      onSuccess: (_, __, ___, { client }) => {
        void spaceObjectInvalidations.deleted(client);
      },
    }),
  /** The inventory's bulk edit (`PUT /spaceobjects/bulk-edit`). The page shows its own toasts. */
  assignPointOfContact: () =>
    mutationOptions<BulkEditResult, Error, PointOfContactAssignment>({
      mutationFn: ({ pointOfContactId, spaceObjectIds }) =>
        spaceObjectsApi.bulkEditSpaceObjects(spaceObjectIds, { pointOfContact: pointOfContactId }),
      meta: { skipSuccessToast: true },
      onSuccess: (_, __, ___, { client }) => {
        void spaceObjectInvalidations.pointOfContactAssigned(client);
      },
    }),
};
